// File: frontend/src/components/dashboard/reputation/LocationsBarChart.js
// Version: 2.0 (Stacked + Improve)
// Date: 2025-12-18 15:40 EST
// Author: Alexander Fitenko
// Description: Stacked Bar Chart (Wins/Losses) with Competitor Tooltip and Improve Actions.

import React from 'react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Legend
} from 'recharts';
import { Trophy, AlertTriangle, Zap } from 'lucide-react';
// COLORS (Pastel Palette)
// COLORS (Modern SaaS Palette)
const COLOR_WIN = "#10b981";  // Emerald-500
const COLOR_LOSS = "#fb7185"; // Rose-400

// Helper to extract City
const getCityFromAddress = (address) => {
    if (!address) return "New Location";
    const parts = address.split(',');
    if (parts.length >= 2) {
        return parts[1].trim();
    }
    return address.length > 20 ? address.substring(0, 20) + '...' : address;
};

const LocationsBarChart = ({ locations, onImprove }) => {
    const data = [...locations].sort((a, b) => b.wins - a.wins);

    // Prepare Data
    const chartData = data.map(l => ({
        ...l,
        city: getCityFromAddress(l.address),
        loss_rate: 100 - l.win_rate // for check
    }));

    // Identify critical locations (Loss > 30%)
    const criticalLocations = chartData.filter(l => l.loss_rate > 30);

    // Custom Tooltip
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const item = payload[0].payload;
            return (
                <div style={{
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '12px',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                    minWidth: '200px'
                }}>
                    <div style={{ fontWeight: '800', color: '#1e293b', marginBottom: '8px', borderBottom: '1px solid #f1f5f9', paddingBottom: '4px' }}>
                        {label}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ color: '#7fc684', fontWeight: '600' }}>Wins</span>
                        <span style={{ fontWeight: '700' }}>{item.wins}</span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span style={{ color: '#e37275', fontWeight: '600' }}>Losses</span>
                        <span style={{ fontWeight: '700' }}>{item.losses}</span>
                    </div>

                    {item.top_competitor && (
                        <div style={{ fontSize: '0.75rem', color: '#64748b', background: '#f8fafc', padding: '6px', borderRadius: '4px', marginTop: '4px' }}>
                            <span style={{ fontWeight: '600', color: '#475569' }}>⚠️ Top Competitor:</span><br />
                            {item.top_competitor}
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="card" style={{ padding: '0', marginBottom: '25px', overflow: 'hidden' }}>
            <div style={{ padding: '24px 24px 10px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: '#334155', fontSize: '1rem' }}>
                    <Trophy size={18} className="text-emerald-500" /> Regional Battlefronts
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '500' }}>Wins vs Losses</div>
            </div>

            <div style={{ height: 350, width: '100%', padding: '0 20px' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        layout="vertical"
                        data={chartData}
                        margin={{ top: 10, right: 30, left: 40, bottom: 20 }}
                        stackOffset="sign"
                    >
                        <XAxis type="number" hide />
                        <YAxis
                            dataKey="city"
                            type="category"
                            width={100}
                            tick={{ fill: '#64748b', fontSize: 12, fontWeight: 500 }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f1f5f9' }} />
                        <Legend verticalAlign="top" height={36} />

                        {/* WINS BAR (EMERALD) - Slim & Rounded */}
                        <Bar dataKey="wins" name="Wins" stackId="a" fill={COLOR_WIN} radius={[4, 0, 0, 4]} barSize={12}>
                            <LabelList
                                dataKey="wins"
                                position="insideLeft"
                                style={{ fill: 'transparent', fontSize: '0px' }} // Hide messy label inside chart
                            />
                        </Bar>

                        {/* LOSSES BAR (ROSE) - Slim & Rounded */}
                        <Bar dataKey="losses" name="Losses" stackId="a" fill={COLOR_LOSS} radius={[0, 4, 4, 0]} barSize={12}>
                            <LabelList
                                dataKey="losses"
                                position="right"
                                style={{ fill: '#94a3b8', fontSize: '11px', fontWeight: '600' }}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>

            {/* IMPROVE FOOTER (Clean Style) */}
            {criticalLocations.length > 0 && (
                <div style={{ background: 'white', padding: '20px 24px', borderTop: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ef4444', fontWeight: '600', fontSize: '0.85rem', marginBottom: '12px' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444' }}></div>
                        High Loss Rates Detected (&gt;30%)
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {criticalLocations.slice(0, 5).map(loc => (
                            <div key={loc.id} style={{
                                background: 'white',
                                borderLeft: '3px solid #ef4444',
                                padding: '8px 12px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}>
                                <div>
                                    <div style={{ fontWeight: '600', fontSize: '0.85rem', color: '#334155' }}>{loc.city}</div>
                                    <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{loc.loss_rate.toFixed(0)}% Losses</div>
                                </div>
                                <button
                                    onClick={() => onImprove && onImprove(loc)}
                                    style={{
                                        background: 'transparent', color: '#ef4444', border: '1px solid #fecaca',
                                        borderRadius: '6px', padding: '4px 8px', fontSize: '0.7rem',
                                        cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <Zap size={10} /> Fix
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocationsBarChart;
